import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Words, FadeUp, PopIn, EASE } from "@/components/Reveal";
import PillButton from "@/components/PillButton";
import { CAUSES } from "@/data/causes";
import { ORGS, ORG_GROUPS } from "@/data/site";

const CauseRow = ({ cause, index }) => (
  <div className={index === 0 ? "pb-8 md:pb-10" : "py-8 md:py-10"}>
    <Link
      to={`/causes/${cause.slug}`}
      data-testid={`causes-row-${cause.slug}`}
      aria-label={`Open issue: ${cause.headline}`}
      className="group grid grid-cols-1 items-center gap-8 rounded-2xl px-2 py-2 transition-colors duration-500 md:grid-cols-[1fr_340px] md:gap-10 md:px-4"
    >
      <div className="flex flex-col gap-3">
        <h3 className="font-display text-[26px] leading-[1.15] text-black transition-transform duration-500 ease-out group-hover:-translate-x-[2px] sm:text-[28px] lg:text-[31px] lg:leading-[37.2px]">
          <Words text={cause.headline} stagger={0.035} />
        </h3>
        <PopIn>
          <span className="inline-flex w-fit items-center rounded-full border border-silver px-4 py-[3px] text-[13px] uppercase leading-[22.4px] tracking-[0.08em] text-charcoal transition-colors group-hover:border-black group-hover:bg-linen sm:text-[14px] font-display">
            {cause.tag}
          </span>
        </PopIn>
        <FadeUp y={16}>
          <p className="max-w-[625px] text-[14.4px] leading-[25.6px] text-charcoal sm:text-[14.6px]">{cause.description}</p>
        </FadeUp>
        <FadeUp y={12}>
          <span className="mt-1 inline-flex items-center font-display text-[15px] leading-[25.6px] text-charcoal sm:text-[16px]">
            <span className="transition-transform duration-500 ease-out group-hover:-translate-x-[2px]">Read the full brief</span>
            <span aria-hidden="true" className="ml-1 transition-transform duration-500 ease-out group-hover:translate-x-1">→</span>
          </span>
        </FadeUp>
      </div>
      <motion.div
        initial={{ clipPath: index % 2 === 0 ? "inset(0% 0% 0% 100%)" : "inset(0% 100% 0% 0%)" }}
        whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1, ease: EASE }}
        className="relative block aspect-[340/255] w-full overflow-hidden rounded-2xl"
      >
        <div className="absolute inset-0 transition-transform duration-[1100ms] group-hover:scale-[1.06]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
          <img alt={cause.imageAlt} className="h-full w-full object-cover" src={cause.image} />
        </div>
        <div className="pointer-events-none absolute inset-0 bg-linen opacity-15 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-0" />
      </motion.div>
    </Link>
  </div>
);

const Causes = () => (
  <main className="bg-white">
    <section className="w-full bg-white pt-[110px] sm:pt-[130px] lg:pt-[150px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto flex max-w-[1208px] flex-col gap-5 lg:px-[60px]">
          <Words text="Causes & Affiliations" mode="load" delay={0.1} className="eyebrow inline-flex overflow-hidden text-[13px] leading-[22.4px] text-charcoal sm:text-[14px]" />
          <h1 className="font-display text-[36px] leading-[1.08] text-black sm:text-[48px] lg:text-[58px] lg:leading-[64px]" aria-label="The Issues She Comes Back To">
            <span className="block">
              <Words text="The Issues She" mode="load" delay={0.2} stagger={0.07} />
            </span>
            <span className="block">
              <Words text="Comes Back To" mode="load" delay={0.36} stagger={0.07} />
            </span>
          </h1>
          <FadeUp mode="load" delay={0.55}>
            <p className="max-w-[620px] text-[15px] leading-[26px] text-charcoal sm:text-[15.6px] sm:leading-[27.2px]">
              Stephanie believes faith should be lived out, not just talked about. These are some of the organizations she proudly supports.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>

    <section className="w-full bg-white py-14 sm:py-16 lg:py-[72px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto max-w-[1208px] lg:px-[60px]">
          <div className="flex flex-col divide-y divide-silver-line">
            {CAUSES.map((cause, i) => (
              <CauseRow key={cause.slug} cause={cause} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="w-full bg-white-smoke py-16 sm:py-20 lg:py-[88px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto flex max-w-[1208px] flex-col gap-12 lg:px-[60px]">
          <div className="flex flex-col gap-4">
            <h2 className="font-display text-[30px] leading-[1.15] text-black sm:text-[36px] lg:text-[42px]">
              <Words text="The people doing the work" />
            </h2>
            <FadeUp y={16}>
              <p className="max-w-[620px] text-[14.6px] leading-[26px] text-charcoal sm:text-[15.2px] sm:leading-[27.2px]">
                Sixteen organizations, grouped by the calling they most serve. Each one earns Stephanie’s time — and deserves yours.
              </p>
            </FadeUp>
          </div>
          <div className="flex flex-col gap-12">
            {ORG_GROUPS.map((g, gi) => (
              <div key={g.title} className="flex flex-col gap-5">
                <FadeUp y={14}>
                  <div className="flex items-center gap-4">
                    <span className="font-display text-[12px] uppercase tracking-[0.22em] text-charcoal-mid">0{gi + 1}</span>
                    <h3 className="font-display text-[24px] leading-[1.2] text-black sm:text-[28px]">{g.title}</h3>
                  </div>
                </FadeUp>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {g.orgs.map((name, oi) => (
                    <FadeUp key={name} delay={0.04 * oi} y={14} className="h-full">
                      <div data-testid={`causes-org-${oi}-${gi}`} className="h-full rounded-2xl border border-hairline bg-white p-6 transition-all duration-500 hover:-translate-y-0.5 hover:border-silver">
                        <p className="font-display text-[18px] leading-[26px] text-black">{name}</p>
                        <p className="mt-2 text-[13.6px] leading-[23px] text-charcoal">{ORGS[name]}</p>
                      </div>
                    </FadeUp>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="w-full bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto flex max-w-[1208px] flex-col items-start gap-6 rounded-3xl bg-linen p-8 sm:p-12 lg:flex-row lg:items-center lg:justify-between lg:px-[60px]">
          <div className="flex max-w-[560px] flex-col gap-3">
            <h2 className="font-display text-[26px] leading-[1.15] text-black sm:text-[32px]">
              <Words text="Stand Alongside the Work" />
            </h2>
            <FadeUp y={14}>
              <p className="text-[14.6px] leading-[26px] text-charcoal-dark sm:text-[15.2px]">
                If these organizations and causes resonate with you, we’d love to have you join Stephanie’s community. You’ll receive occasional updates about
                new podcast episodes, community initiatives, and opportunities to support work that’s making a real difference.
              </p>
            </FadeUp>
          </div>
          <FadeUp y={12}>
            <PillButton href="/community" dataTestId="causes-join-button">
              Join the Community
            </PillButton>
          </FadeUp>
        </div>
      </div>
    </section>
  </main>
);

export default Causes;
